import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import type {
  ParticipantSession,
  ParticipantStep,
  ParticipantView,
} from "../../shared/types.ts";
import { QuestionPrompt } from "../../../vendor/member-core/components/QuestionPrompt.tsx";
import { ReflectionCard } from "../../../vendor/member-core/components/ReflectionCard.tsx";
import { ProcessingDots } from "../../../vendor/member-core/components/ProcessingDots.tsx";
import { ScaleVisual } from "../../../vendor/member-core/components/ScaleVisual.tsx";
import { RecordButton } from "../../../vendor/member-core/components/ui/RecordButton.tsx";
import {
  AnswerDock,
  type AnswerDockState,
} from "../../../vendor/member-frontend/components/session-phases/AnswerDock.tsx";
import {
  DOCK_TEXT_ACTION,
  SpeakStatus,
} from "../../../vendor/member-frontend/components/session-phases/AnswerDockParts.tsx";
import { TextInputInterface } from "../../../vendor/member-frontend/components/TextInputInterface.tsx";
import { focusMemberControl } from "../../../vendor/member-core/lib/memberFocus.ts";
import { Eyebrow, RichText } from "./ParticipantFields.tsx";
import { LocalAudioPlayer } from "../media/LocalAudioPlayer.tsx";
import "./participant-fidelity.css";

export type InputMode = ParticipantSession["inputMode"];
export interface ConversationStepProps {
  mediaIdentity: string;
  step: ParticipantStep;
  phase: ParticipantSession["phase"];
  response?: ParticipantSession["responses"][string];
  responseOptions?: ParticipantView["responseOptions"];
  mode: InputMode;
  readOnly: boolean;
  paused: boolean;
  controlRevision: number;
  waiting: boolean;
  pending: boolean;
  armed: boolean;
  focusTarget: (element: HTMLElement | null) => void;
  continueButton: ReactNode;
  frame: (
    content: ReactNode,
    dock?: ReactNode,
    centered?: boolean,
  ) => ReactNode;
  onRespond: (variantId?: string) => void;
  onAdvance: () => void;
  onMode: (mode: InputMode) => void;
}

/** Source presentation; only the service boundary is adapted to authored JSON. */
export function ConversationStep(props: ConversationStepProps) {
  const { step, phase, mode, readOnly, paused, controlRevision, focusTarget } =
    props;
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  // AnswerDock suppresses its initial children's entrance animations. Enter
  // through rest even when resuming directly on an accepted response, so the
  // source ContinueButton runs its entrance and releases its reveal latch.
  const [dockMounted, setDockMounted] = useState(false);
  useEffect(() => setDockMounted(true), []);
  const [selected, setSelected] = useState("");
  const [draft, setDraft] = useState<string | null>(null);
  const [inputError, setInputError] = useState("");
  const choiceId = useId(),
    dockRef = useRef<HTMLFieldSetElement>(null),
    voiceFocusRequested = useRef(false);
  const choice =
    props.responseOptions?.find((option) => option.id === selected) ??
    props.responseOptions?.[0];
  const unavailable = readOnly || paused || props.waiting || !props.armed;
  const answered = phase === "prompt" ? undefined : props.response;
  const reflection = phase === "reflection" ? answered?.reflection : undefined;
  const prepared = (choice?.input ?? "").replaceAll("__", "");
  const dockState: AnswerDockState =
    !dockMounted || props.pending
      ? "rest"
      : phase !== "prompt"
        ? "reflection"
        : mode === "text"
          ? "text"
          : "voice";

  // Text belongs to the question, surviving modality and connection changes.
  // Losing input ownership discards it, including an offline takeover cycle.
  useEffect(() => {
    setDraft(null);
    setInputError("");
    voiceFocusRequested.current = false;
  }, [controlRevision, readOnly]);
  useEffect(() => {
    if (unavailable || phase !== "prompt") return;
    const control = dockRef.current?.querySelector<
      HTMLTextAreaElement | HTMLButtonElement
    >(
      mode === "text"
        ? '[data-testid="text-answer-input"]'
        : '[data-testid="record-button"]',
    );
    if (!control) return;
    if (control instanceof HTMLTextAreaElement)
      return focusMemberControl(control);
    // A modality switch returns focus to the control the participant requested.
    if (voiceFocusRequested.current) {
      voiceFocusRequested.current = false;
      control.focus({ preventScroll: true });
    }
  }, [mode, unavailable, phase]);

  const submitText = (text: string) => {
    if (unavailable) return;
    const offered = props.responseOptions?.find(
      (option) => option.input?.replaceAll("__", "").trim() === text.trim(),
    );
    if (!offered) {
      setInputError("Choose one of the available responses to continue.");
      return;
    }
    setInputError("");
    props.onRespond(offered.id);
  };
  const content = props.pending ? (
    <div
      className="pe-processing"
      role="status"
      aria-label="Preparing the response"
    >
      <ProcessingDots />
    </div>
  ) : (
    <>
      {phase === "prompt" && (
        <>
          <Eyebrow step={step} />
          <div className="pe-question" tabIndex={-1} ref={focusTarget}>
            <QuestionPrompt
              questionText={(step.heading ?? step.title).replaceAll("__", "")}
              topic={step.sectionTitle}
              isFollowup={false}
              isRecording={false}
              guidanceText={
                (step.body ?? step.scaleContext?.guidance)?.replaceAll(
                  "__",
                  "",
                ) ?? null
              }
            />
          </div>
        </>
      )}
      {step.scaleContext && (
        <ConversationScale step={step} score={answered?.score} />
      )}
      {phase === "response" && answered && (
        <>
          <figure
            className="pe-response"
            data-mode={mode}
            tabIndex={-1}
            ref={focusTarget}
          >
            <blockquote className="pe-response__text">
              <RichText text={answered.text} />
            </blockquote>
            {step.audio && (
              <LocalAudioPlayer
                className="pe-response__audio"
                identity={props.mediaIdentity}
                src={step.audio}
                label="Your response"
                disabled={paused}
              />
            )}
          </figure>
        </>
      )}
      {reflection && (
        <div className="pe-reflection" tabIndex={-1} ref={focusTarget}>
          <ReflectionCard
            reflectionText={reflection}
            onContinue={props.onAdvance}
            continueDisabled={
              readOnly || paused || props.waiting || !props.armed
            }
            continueContainer={slot}
            inset="dock"
          />
        </div>
      )}
    </>
  );
  const dock = (
    <div className="pe-source-dock">
      {phase === "prompt" &&
        props.responseOptions &&
        props.responseOptions.length > 1 && (
          <label htmlFor={choiceId} className="pe-authored-choice">
            Response
            <select
              id={choiceId}
              value={choice?.id}
              disabled={unavailable}
              onChange={(event) => {
                setSelected(event.target.value);
                setDraft(null);
                setInputError("");
              }}
            >
              {props.responseOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        )}
      {inputError && (
        <div className="pe-authored-error" role="alert">
          <p>{inputError}</p>
          <button
            type="button"
            onClick={() => {
              setDraft(null);
              setInputError("");
            }}
          >
            Use selected response
          </button>
        </div>
      )}
      <fieldset
        ref={dockRef}
        className="pe-source-dock__slot"
        disabled={unavailable}
      >
        <AnswerDock
          state={dockState}
          mark={
            dockState === "voice" ? (
              <RecordButton
                isRecording={false}
                durationMs={0}
                showDuration={false}
                disabled={unavailable || !choice}
                onTap={() => props.onRespond(choice?.id)}
              />
            ) : undefined
          }
        >
          {dockState === "voice" ? (
            <>
              <SpeakStatus hint="Tap to speak" />
              <button
                type="button"
                data-testid="switch-to-text"
                className={DOCK_TEXT_ACTION}
                onClick={() => props.onMode("text")}
              >
                Type instead
              </button>
            </>
          ) : dockState === "text" ? (
            <TextInputInterface
              questionText={(step.heading ?? step.title).replaceAll("__", "")}
              value={draft ?? prepared}
              onValueChange={(text) => {
                setDraft(text);
                setInputError("");
              }}
              onSubmit={submitText}
              micAvailable={
                !readOnly && !paused && step.captureMode !== "text_only"
              }
              onSwitchToVoice={() => {
                voiceFocusRequested.current = true;
                props.onMode("voice");
              }}
              isSubmitting={unavailable}
            />
          ) : dockState === "reflection" ? (
            reflection ? (
              <div
                ref={setSlot}
                className="flex justify-center"
                data-dock-continue-slot
              />
            ) : (
              props.continueButton
            )
          ) : null}
        </AnswerDock>
      </fieldset>
    </div>
  );
  return props.frame(content, dock, true);
}

/** A spoken scale is context, never a second answer control. */
function ConversationScale({
  step,
  score,
}: {
  step: ParticipantStep;
  score?: number;
}) {
  const labelId = useId();
  const scale = step.scaleContext!;
  const min = scale.min ?? 0,
    max = scale.max ?? 10;
  const accepted = typeof score === "number" && Number.isFinite(score);
  return (
    <section
      className="pe-conversation-scale"
      data-source-control="scale"
      data-testid="conversation-scale"
      aria-labelledby={labelId}
    >
      <p id={labelId} className={accepted ? "pe-heard-score" : "pe-sr"}>
        {accepted ? (
          <span data-testid="heard-score">
            Your score: <strong>{score}</strong> out of {max}
          </span>
        ) : (
          `Scale from ${min} to ${max}`
        )}
      </p>
      <ScaleVisual
        key={step.id + ":" + (accepted ? score : "unanswered")}
        config={{
          min,
          max,
          step: 1,
          min_label: scale.lowLabel,
          max_label: scale.highLabel,
        }}
        mode="static"
        score={accepted ? score : null}
        animateEntrance={false}
        anchorId="conversation-reference-scale"
      />
    </section>
  );
}
